import {ErrorMessage, FieldArray, Form, Formik} from 'formik'
import {isEmpty} from 'lodash'
import moment from 'moment'
import {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {toast} from 'react-toastify'
import Modal from 'rsuite/Modal'
import * as Yup from 'yup'
import * as place from 'src/app/modules/common'
import * as nabilAuctionRedux from '../index'
import FormTextBox from 'src/cms/helpers/components/forms/FormTextBox'
import FormDatepicker from 'src/cms/helpers/components/forms/FormDatepicker'
import FormTinyMce from 'src/cms/helpers/components/forms/FormTinyMce'
import FormRadio from 'src/cms/helpers/components/forms/FormRadio'
import FormSelect from 'src/cms/helpers/components/forms/FormSelect'
import FormInputMediaManager from 'src/cms/helpers/components/forms/FormInputMediaManager'
import MapView from 'src/app/modules/common/components/Map/MapView'
import {StateParamsModel} from 'src/app/modules/common/Model'

type Props = {
  open: boolean
  params?: StateParamsModel
  handleClose: () => void
  actionType: string
  editSelectedData?: any
}

const PROPERTY_TYPE_OPTIONS = [
  {label: 'Residential', value: 'Residential'},
  {label: 'Commercial', value: 'Commercial'},
]

const FORM_VALIDATION = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  nabil_auction_category_id: Yup.string().required('Category is required'),
  province_id: Yup.string().required('Province is required'),
  district_id: Yup.string().required('District is required'),
  publish_date: Yup.date().required('Publish date is required'),
  bank_contact_name: Yup.string().required('Contact name is required'),
  bank_contact_no: Yup.string().required('Contact number is required'),
  status: Yup.string().required('Status is required'),
})

const AddNabilAuction = ({open, params, handleClose, actionType, editSelectedData}: Props) => {
  const dispatch = useDispatch()
  const [provinceValue, setProvinceValue] = useState('')
  const [districtValue, setDistrictValue] = useState('')

  const nabilAuctionState = useSelector((state: any) => state.nabilAuction)
  const {loading, success} = nabilAuctionState
  const categories = nabilAuctionState?.data?.categoryList || []

  const {district, province, municipality} = useSelector((state: any) => state.place)
  const provinceOptions = province?.map((item: any) => ({label: item.title, value: item.id})) ?? []
  const districtOptions = district?.map((item: any) => ({label: item.title, value: item.id})) ?? []
  const municipalityOptions =
    municipality?.map((item: any) => ({label: item.title, value: item.id})) ?? []

  useEffect(() => {
    dispatch(place.action.getProvince())
    dispatch(nabilAuctionRedux.actions.getNabilAuctionCategoryList())
  }, [])

  useEffect(() => {
    if (provinceValue) dispatch(place.action.getDistrict(provinceValue))
  }, [provinceValue])

  useEffect(() => {
    if (districtValue) dispatch(place.action.getMunicipality(districtValue))
  }, [districtValue])

  useEffect(() => {
    if (!isEmpty(editSelectedData)) {
      if (editSelectedData.province_id) setProvinceValue(editSelectedData.province_id)
      if (editSelectedData.district_id) setDistrictValue(editSelectedData.district_id)
    }
  }, [editSelectedData])

  useEffect(() => {
    if (success) {
      isEmpty(editSelectedData)
        ? toast.success('Nabil Auction created successfully.')
        : toast.success('Nabil Auction updated successfully.')
      dispatch(nabilAuctionRedux.actions.resetNabilAuction())
      handleClose()
      dispatch(nabilAuctionRedux.actions.getNabilAuction(params))
    }
  }, [success])

  return (
    <div className='modal-container'>
      <Modal
        open={open}
        onClose={handleClose}
        className='w-75'
        backdrop='static'
        keyboard={false}
        enforceFocus={false}
        data-cy='modal'
      >
        <Modal.Header>
          <Modal.Title>{actionType} Nabil Auction</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <Formik
              enableReinitialize
              initialValues={{
                name: '',
                name_np: '',
                slug: '',
                nabil_auction_category_id: editSelectedData?.nabil_auction_category_id ?? '',
                province_id: editSelectedData?.province_id ?? '',
                district_id: editSelectedData?.district_id ?? '',
                municipality_id: editSelectedData?.municipality_id ?? '',
                description: '',
                description_np: '',
                image: '',
                image_np: '',
                file: '',
                file_np: '',
                attached_file_title: '',
                attached_file_title_np: '',
                publish_date: null,
                publish_time: null,
                expiry_date: null,
                expiry_time: null,
                notification_days: '',
                city: '',
                latitude: '',
                longitude: '',
                road_access: '',
                bank_contact_name: '',
                bank_contact_no: '',
                minimum_price: '',
                property_type: '',
                land_system: '',
                ropani: '',
                anna: '',
                paisa: '',
                dam: '',
                biga: '',
                katha: '',
                dhur: '',
                vehicle_type: '',
                fuel_type: '',
                tax_status: '',
                model_no: '',
                lot_no: '',
                make_year: '',
                registration_number: '',
                chesis: '',
                engine: '',
                additional_images: [''],
                status: 'Active',
              }}
              validationSchema={FORM_VALIDATION}
              onSubmit={(values) => {
                const slug = !isEmpty(editSelectedData)
                  ? editSelectedData.slug || values.name.replace(/\s+/g, '-').toLowerCase()
                  : values.name.replace(/\s+/g, '-').toLowerCase()
                const formData = {
                  ...values,
                  slug,
                  status: values.status === 'Active',
                  additional_images: values.additional_images.filter(Boolean),
                  minimum_price: values.minimum_price ? Number(values.minimum_price) : null,
                  publish_date: values.publish_date
                    ? moment(values.publish_date).format('YYYY-MM-DD')
                    : null,
                  publish_time: values.publish_time
                    ? moment(values.publish_time).format('HH:mm:ss')
                    : null,
                  expiry_date: values.expiry_date
                    ? moment(values.expiry_date).format('YYYY-MM-DD')
                    : null,
                  expiry_time: values.expiry_time
                    ? moment(values.expiry_time).format('HH:mm:ss')
                    : null,
                }
                if (!isEmpty(editSelectedData)) {
                  dispatch(
                    nabilAuctionRedux.actions.updateNabilAuction(formData, editSelectedData?.id)
                  )
                } else {
                  dispatch(nabilAuctionRedux.actions.addNabilAuction(formData))
                }
              }}
            >
              {({errors, touched, handleChange, values, setFieldValue}) => {
                useEffect(() => {
                  if (!isEmpty(editSelectedData)) {
                    const textFields = [
                      'name',
                      'name_np',
                      'nabil_auction_category_id',
                      'province_id',
                      'district_id',
                      'municipality_id',
                      'description',
                      'description_np',
                      'image',
                      'image_np',
                      'file',
                      'file_np',
                      'attached_file_title',
                      'attached_file_title_np',
                      'notification_days',
                      'city',
                      'latitude',
                      'longitude',
                      'road_access',
                      'bank_contact_name',
                      'bank_contact_no',
                      'minimum_price',
                      'property_type',
                      'land_system',
                      'ropani',
                      'anna',
                      'paisa',
                      'dam',
                      'biga',
                      'katha',
                      'dhur',
                      'vehicle_type',
                      'fuel_type',
                      'tax_status',
                      'model_no',
                      'lot_no',
                      'make_year',
                      'registration_number',
                      'chesis',
                      'engine',
                    ]
                    textFields.forEach((f) => setFieldValue(f, editSelectedData[f] ?? '', false))
                    // Date fields: convert string to Date object (datepicker requires Date | null)
                    ;(['publish_date', 'expiry_date'] as const).forEach((f) => {
                      const raw = editSelectedData[f]
                      if (raw) {
                        const d = moment(raw, 'YYYY-MM-DD')
                        setFieldValue(f, d.isValid() ? d.toDate() : null, false)
                      }
                    })
                    // Time fields: leave as null — raw strings crash datepicker
                    setFieldValue('publish_time', null, false)
                    setFieldValue('expiry_time', null, false)
                    setFieldValue(
                      'status',
                      editSelectedData.status ? 'Active' : 'Inactive',
                      false
                    )
                    setFieldValue(
                      'additional_images',
                      editSelectedData.auction_images?.length > 0
                        ? editSelectedData.auction_images
                        : [''],
                      false
                    )
                  }
                }, [])

                const selectedCategorySlug = categories.find(
                  (c: any) => c.id === values.nabil_auction_category_id
                )?.slug
                const isVehicle = selectedCategorySlug === 'vehicle'
                const isRopani = values.land_system === 'ropani'
                const isBiga = values.land_system === 'biga'

                return (
                  <Form>
                    <div className='card-body border-top pt-5'>
                      <div className='row'>
                        {/* ── Basic Info ── */}
                        <div className='col-md-6 col-xs-12'>
                          <FormSelect
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            placeholder='Select Category'
                            label='Category'
                            name='nabil_auction_category_id'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            options={categories.map((c: any) => ({label: c.name, value: c.id}))}
                            values={values}
                            setFieldValue={setFieldValue}
                            required
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            type='text'
                            placeholder='Enter Name'
                            name='name'
                            label='Name (EN)'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            required
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            type='text'
                            placeholder='नाम प्रविष्ट गर्नुहोस्'
                            name='name_np'
                            label='Name (NP)'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            type='text'
                            name='slug'
                            label='Slug'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            disabled
                            value={
                              !isEmpty(editSelectedData)
                                ? editSelectedData?.slug ?? ''
                                : values.name.replace(/\s+/g, '-').toLowerCase()
                            }
                          />
                        </div>

                        {/* ── Location ── */}
                        <div className='col-md-6 col-xs-12'>
                          <FormSelect
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            placeholder='Select Province'
                            label='Province'
                            name='province_id'
                            onChange={(e: any) => {
                              handleChange(e)
                              setProvinceValue(e.target?.value || '')
                              setFieldValue('district_id', '')
                              setFieldValue('municipality_id', '')
                            }}
                            errors={errors}
                            touched={touched}
                            options={provinceOptions}
                            values={values}
                            setFieldValue={setFieldValue}
                            required
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormSelect
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            placeholder='Select District'
                            label='District'
                            name='district_id'
                            disabled={!values.province_id}
                            onChange={(e: any) => {
                              handleChange(e)
                              setDistrictValue(e.target?.value || '')
                              setFieldValue('municipality_id', '')
                            }}
                            errors={errors}
                            touched={touched}
                            options={districtOptions}
                            values={values}
                            setFieldValue={setFieldValue}
                            required
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormSelect
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            placeholder='Select Municipality'
                            label='Municipality'
                            name='municipality_id'
                            disabled={!values.district_id}
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            options={municipalityOptions}
                            values={values}
                            setFieldValue={setFieldValue}
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            type='text'
                            placeholder='Enter City'
                            name='city'
                            label='City'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                          />
                        </div>
                        <div className='col-12'>
                          <label className='form-label fw-bolder text-dark fs-6 mb-2'>
                            Location on Map
                          </label>
                          <MapView
                            latName='latitude'
                            lngName='longitude'
                            location={{
                              lat: values.latitude || '27.7172',
                              lng: values.longitude || '85.3240',
                            }}
                            setFieldValue={setFieldValue}
                          />
                        </div>
                        <div className='col-md-3 col-xs-12'>
                          <FormTextBox
                            type='text'
                            placeholder='e.g. 27.7172'
                            name='latitude'
                            label='Latitude'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                          />
                        </div>
                        <div className='col-md-3 col-xs-12'>
                          <FormTextBox
                            type='text'
                            placeholder='e.g. 85.3240'
                            name='longitude'
                            label='Longitude'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                          />
                        </div>

                        {/* ── Publish / Expiry ── */}
                        <div className='col-md-6 col-xs-12'>
                          <FormDatepicker
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            dateFormat='yyyy-MM-dd'
                            name='publish_date'
                            label='Publish Date'
                            setFieldValue={setFieldValue}
                            value={values.publish_date || null}
                            errors={errors}
                            touched={touched}
                            required
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormDatepicker
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            dateFormat='hh:mm aa'
                            showTimeSelect
                            showTimeSelectOnly
                            name='publish_time'
                            label='Publish Time'
                            setFieldValue={setFieldValue}
                            value={values.publish_time || null}
                            errors={errors}
                            touched={touched}
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormDatepicker
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            dateFormat='yyyy-MM-dd'
                            name='expiry_date'
                            label='Expiry Date'
                            setFieldValue={setFieldValue}
                            value={values.expiry_date || null}
                            errors={errors}
                            touched={touched}
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormDatepicker
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            dateFormat='hh:mm aa'
                            showTimeSelect
                            showTimeSelectOnly
                            name='expiry_time'
                            label='Expiry Time'
                            setFieldValue={setFieldValue}
                            value={values.expiry_time || null}
                            errors={errors}
                            touched={touched}
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            type='number'
                            placeholder='Notification days'
                            name='notification_days'
                            label='Notification Days'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                          />
                        </div>

                        {/* ── Contact & Pricing ── */}
                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            type='text'
                            placeholder='Bank Contact Name'
                            name='bank_contact_name'
                            label='Bank Contact Name'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            required
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            type='text'
                            placeholder='Bank Contact Number'
                            name='bank_contact_no'
                            label='Bank Contact No'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            required
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            type='number'
                            placeholder='Minimum Price'
                            name='minimum_price'
                            label='Minimum Price'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                          />
                        </div>

                        {/* ── Descriptions ── */}
                        <div className='col-md-6 col-xs-12'>
                          <FormTinyMce
                            name='description'
                            handleChange={(e: any) => setFieldValue('description', e)}
                            value={values.description}
                            label='Description (EN)'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormTinyMce
                            name='description_np'
                            handleChange={(e: any) => setFieldValue('description_np', e)}
                            value={values.description_np}
                            label='Description (NP)'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                          />
                        </div>

                        {/* ── Images ── */}
                        <div className='col-md-6 col-xs-12'>
                          <FormInputMediaManager
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            label='Main Image (EN)'
                            name='image'
                            setFieldValue={setFieldValue}
                            value={values.image}
                            showImageOnly
                            buttonName='Add Image'
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormInputMediaManager
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            label='Main Image (NP)'
                            name='image_np'
                            setFieldValue={setFieldValue}
                            value={values.image_np}
                            showImageOnly
                            buttonName='Add Image'
                          />
                        </div>

                        {/* ── Files ── */}
                        <div className='col-md-6 col-xs-12'>
                          <FormInputMediaManager
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            label='Attached File (EN)'
                            name='file'
                            setFieldValue={setFieldValue}
                            value={values.file}
                            buttonName='Add File'
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            type='text'
                            name='attached_file_title'
                            label='File Title (EN)'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            placeholder='Enter file title'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormInputMediaManager
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            label='Attached File (NP)'
                            name='file_np'
                            setFieldValue={setFieldValue}
                            value={values.file_np}
                            buttonName='Add File'
                          />
                        </div>
                        <div className='col-md-6 col-xs-12'>
                          <FormTextBox
                            type='text'
                            name='attached_file_title_np'
                            label='File Title (NP)'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            placeholder='Enter file title (Nepali)'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                          />
                        </div>

                        {/* ── Additional Images ── */}
                        <div className='col-12 mt-4'>
                          <FieldArray
                            name='additional_images'
                            render={(arrayHelpers) => (
                              <div>
                                <h6 className='mb-3'>Additional Images</h6>
                                {values.additional_images.map((imgPath: string, index: number) => (
                                  <div key={index} className='d-flex align-items-center mb-3 gap-3'>
                                    <div className='flex-grow-1'>
                                      <FormInputMediaManager
                                        labelClassName='col-md-12'
                                        containerClassName='col-md-12'
                                        name={`additional_images[${index}]`}
                                        setFieldValue={setFieldValue}
                                        value={imgPath}
                                        showImageOnly
                                        buttonName='Add Image'
                                      />
                                    </div>
                                    {index > 0 && (
                                      <button
                                        type='button'
                                        className='btn btn-sm btn-secondary'
                                        onClick={() => arrayHelpers.remove(index)}
                                      >
                                        Remove
                                      </button>
                                    )}
                                  </div>
                                ))}
                                {values.additional_images[
                                  values.additional_images.length - 1
                                ] && (
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-primary'
                                    onClick={() => arrayHelpers.push('')}
                                  >
                                    + Add More
                                  </button>
                                )}
                              </div>
                            )}
                          />
                        </div>

                        {/* ── Vehicle Fields ── */}
                        {isVehicle && (
                          <div className='col-12 mt-3'>
                            <div className='row border rounded p-3 mb-3'>
                              <h6 className='col-12 mb-3'>Vehicle Details</h6>
                              <div className='col-md-6'>
                                <FormSelect
                                  label='Vehicle Type'
                                  name='vehicle_type'
                                  labelClassName='col-md-12'
                                  containerClassName='col-md-12'
                                  placeholder='Select Vehicle Type'
                                  options={[
                                    {label: 'Private', value: 'Private'},
                                    {label: 'Commercial', value: 'Commercial'},
                                  ]}
                                  onChange={handleChange}
                                  errors={errors}
                                  touched={touched}
                                  values={values}
                                  setFieldValue={setFieldValue}
                                />
                              </div>
                              <div className='col-md-6'>
                                <FormSelect
                                  label='Fuel Type'
                                  name='fuel_type'
                                  labelClassName='col-md-12'
                                  containerClassName='col-md-12'
                                  placeholder='Select Fuel Type'
                                  options={[
                                    {label: 'EV', value: 'EV'},
                                    {label: 'Petrol', value: 'Petrol'},
                                  ]}
                                  onChange={handleChange}
                                  errors={errors}
                                  touched={touched}
                                  values={values}
                                  setFieldValue={setFieldValue}
                                />
                              </div>
                              <div className='col-md-6'>
                                <FormSelect
                                  label='Tax Status'
                                  name='tax_status'
                                  labelClassName='col-md-12'
                                  containerClassName='col-md-12'
                                  placeholder='Select Tax Status'
                                  options={[
                                    {label: 'Paid', value: 'Paid'},
                                    {label: 'Not Paid', value: 'Not Paid'},
                                  ]}
                                  onChange={handleChange}
                                  errors={errors}
                                  touched={touched}
                                  values={values}
                                  setFieldValue={setFieldValue}
                                />
                              </div>
                              <div className='col-md-6'>
                                <FormTextBox
                                  label='Model No'
                                  name='model_no'
                                  type='text'
                                  labelClassName='col-md-12'
                                  containerClassName='col-md-12'
                                  placeholder='Enter Model No'
                                  onChange={handleChange}
                                  errors={errors}
                                  touched={touched}
                                />
                              </div>
                              <div className='col-md-6'>
                                <FormTextBox
                                  label='Lot No'
                                  name='lot_no'
                                  type='text'
                                  labelClassName='col-md-12'
                                  containerClassName='col-md-12'
                                  placeholder='Enter Lot No'
                                  onChange={handleChange}
                                  errors={errors}
                                  touched={touched}
                                />
                              </div>
                              <div className='col-md-6'>
                                <FormTextBox
                                  label='Make Year'
                                  name='make_year'
                                  type='text'
                                  labelClassName='col-md-12'
                                  containerClassName='col-md-12'
                                  placeholder='e.g. 2020'
                                  onChange={handleChange}
                                  errors={errors}
                                  touched={touched}
                                />
                              </div>
                              <div className='col-md-6'>
                                <FormTextBox
                                  label='Registration Number'
                                  name='registration_number'
                                  type='text'
                                  labelClassName='col-md-12'
                                  containerClassName='col-md-12'
                                  placeholder='Enter Registration Number'
                                  onChange={handleChange}
                                  errors={errors}
                                  touched={touched}
                                />
                              </div>
                              <div className='col-md-6'>
                                <FormTextBox
                                  label='Chassis Number'
                                  name='chesis'
                                  type='text'
                                  labelClassName='col-md-12'
                                  containerClassName='col-md-12'
                                  placeholder='Enter Chassis Number'
                                  onChange={handleChange}
                                  errors={errors}
                                  touched={touched}
                                />
                              </div>
                              <div className='col-md-6'>
                                <FormTextBox
                                  label='Engine Number'
                                  name='engine'
                                  type='text'
                                  labelClassName='col-md-12'
                                  containerClassName='col-md-12'
                                  placeholder='Enter Engine Number'
                                  onChange={handleChange}
                                  errors={errors}
                                  touched={touched}
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* ── Property Fields (non-vehicle) ── */}
                        {!isVehicle && (
                          <div className='col-12 mt-3'>
                            <div className='row border rounded p-3 mb-3'>
                              <h6 className='col-12 mb-3'>Property Details</h6>
                              <div className='col-md-6'>
                                <FormSelect
                                  label='Property Type'
                                  name='property_type'
                                  labelClassName='col-md-12'
                                  containerClassName='col-md-12'
                                  placeholder='Select Property Type'
                                  options={PROPERTY_TYPE_OPTIONS}
                                  onChange={handleChange}
                                  errors={errors}
                                  touched={touched}
                                  values={values}
                                  setFieldValue={setFieldValue}
                                />
                              </div>
                              <div className='col-md-6'>
                                <FormTextBox
                                  label='Road Access'
                                  name='road_access'
                                  type='text'
                                  labelClassName='col-md-12'
                                  containerClassName='col-md-12'
                                  placeholder='Describe road access'
                                  onChange={handleChange}
                                  errors={errors}
                                  touched={touched}
                                />
                              </div>
                              <div className='col-md-6'>
                                <label className='form-label fw-bolder fs-6'>Land System</label>
                                <div className='d-flex ms-5'>
                                  {[
                                    {label: 'Ropani', value: 'ropani'},
                                    {label: 'Biga', value: 'biga'},
                                  ].map((opt) => (
                                    <FormRadio
                                      key={opt.value}
                                      containerClassName=''
                                      label='Land System'
                                      name='land_system'
                                      type='radio'
                                      onChange={handleChange}
                                      errors={errors}
                                      touched={touched}
                                      checkBoxText={opt.label}
                                      value={opt.value}
                                    />
                                  ))}
                                </div>
                              </div>
                              {isRopani && (
                                <>
                                  <div className='col-md-3'>
                                    <FormTextBox
                                      label='Ropani'
                                      name='ropani'
                                      type='number'
                                      labelClassName='col-md-12'
                                      containerClassName='col-md-12'
                                      placeholder='0'
                                      onChange={handleChange}
                                      errors={errors}
                                      touched={touched}
                                    />
                                  </div>
                                  <div className='col-md-3'>
                                    <FormTextBox
                                      label='Anna'
                                      name='anna'
                                      type='number'
                                      labelClassName='col-md-12'
                                      containerClassName='col-md-12'
                                      placeholder='0'
                                      onChange={handleChange}
                                      errors={errors}
                                      touched={touched}
                                    />
                                  </div>
                                  <div className='col-md-3'>
                                    <FormTextBox
                                      label='Paisa'
                                      name='paisa'
                                      type='number'
                                      labelClassName='col-md-12'
                                      containerClassName='col-md-12'
                                      placeholder='0'
                                      onChange={handleChange}
                                      errors={errors}
                                      touched={touched}
                                    />
                                  </div>
                                  <div className='col-md-3'>
                                    <FormTextBox
                                      label='Dam'
                                      name='dam'
                                      type='number'
                                      labelClassName='col-md-12'
                                      containerClassName='col-md-12'
                                      placeholder='0'
                                      onChange={handleChange}
                                      errors={errors}
                                      touched={touched}
                                    />
                                  </div>
                                </>
                              )}
                              {isBiga && (
                                <>
                                  <div className='col-md-4'>
                                    <FormTextBox
                                      label='Biga'
                                      name='biga'
                                      type='number'
                                      labelClassName='col-md-12'
                                      containerClassName='col-md-12'
                                      placeholder='0'
                                      onChange={handleChange}
                                      errors={errors}
                                      touched={touched}
                                    />
                                  </div>
                                  <div className='col-md-4'>
                                    <FormTextBox
                                      label='Katha'
                                      name='katha'
                                      type='number'
                                      labelClassName='col-md-12'
                                      containerClassName='col-md-12'
                                      placeholder='0'
                                      onChange={handleChange}
                                      errors={errors}
                                      touched={touched}
                                    />
                                  </div>
                                  <div className='col-md-4'>
                                    <FormTextBox
                                      label='Dhur'
                                      name='dhur'
                                      type='number'
                                      labelClassName='col-md-12'
                                      containerClassName='col-md-12'
                                      placeholder='0'
                                      onChange={handleChange}
                                      errors={errors}
                                      touched={touched}
                                    />
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        )}

                        {/* ── Status ── */}
                        <div className='col-md-6 col-xs-12'>
                          <label className='form-label fw-bolder text-dark fs-6 required'>
                            Status
                          </label>
                          <div className='d-flex ms-5'>
                            {[
                              {label: 'Active', value: 'Active'},
                              {label: 'Inactive', value: 'Inactive'},
                            ].map((opt) => (
                              <FormRadio
                                key={opt.value}
                                containerClassName=''
                                label='Status'
                                name='status'
                                type='radio'
                                onChange={handleChange}
                                errors={errors}
                                touched={touched}
                                checkBoxText={opt.label}
                                value={opt.value}
                              />
                            ))}
                          </div>
                          <div className='fv-plugins-message-container text-danger fw-bolder small text-end mt-1'>
                            <ErrorMessage
                              name='status'
                              component='div'
                              className='field-error-message'
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className='d-flex justify-content-end px-9'>
                      <button
                        type='submit'
                        disabled={loading}
                        className='btn btn-primary btn-sm ms-3'
                      >
                        Save
                      </button>
                      <button
                        type='button'
                        onClick={handleClose}
                        className='btn btn-secondary btn-sm ms-3'
                      >
                        Cancel
                      </button>
                    </div>
                  </Form>
                )
              }}
            </Formik>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  )
}

export default AddNabilAuction
